import { useAuth } from '@agentnet/auth';
import { EmptyState } from '@fluentsms/agentnet-web-components';
import { DoneEmptyState, ErrorEmptyState, StopEmptyState } from '@fluentsms/agentnet-web-icons';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { doIdentityVerification } from 'api/verification/verification-api';
import {
  completedText,
  furtherAssistanceText,
  requestTimeOutHelpText,
  requestTimeOutText,
  unexpectedErrorHelpText,
  unexpectedErrorText,
} from 'features/help/constants';
import useAsync from 'hooks/useAsync';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import Logo from 'ui-kit/icons/AgentNetLogo';
import { IdentityVerificationResponse, TransactionStatusEnum } from './types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentWrap: {
      display: 'flex',
      height: 'calc(100%)',
    },
    headerBar: {
      background: theme.palette.brand.legacy,
      height: 65,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(0, 3),
    },
  }),
);
const IdentityVerification = () => {
  const classes = useStyles();
  const { getAccessToken } = useAuth();

  const { tokenId } = useParams<{ tokenId: string }>();

  const getIdentityVerificationData = async (): Promise<IdentityVerificationResponse> => {
    const authToken = await getAccessToken();

    return doIdentityVerification(tokenId ?? '', authToken) as Promise<IdentityVerificationResponse>;
  };
  const {
    execute: executeIdentityVerification,
    value: identityVerificationData,
    status: identityVerificationStatus,
  } = useAsync<IdentityVerificationResponse>(getIdentityVerificationData, false);

  useEffect(() => {
    executeIdentityVerification();
  }, []);

  const { transactionStatus, data } = identityVerificationData || {};

  useEffect(() => {
    if (transactionStatus === TransactionStatusEnum.Pending) {
      if (data?.captureUrl) {
        window.location.href = data.captureUrl;
      }
    }
  }, [identityVerificationData]);

  const CustomOverlayLoading = () => {
    return (
      <div className="files-container">
        <div className="no-rows">
          <LoadingSpinner status="pending" className="files-container--spinner-root" />
        </div>
      </div>
    );
  };

  const renderStatus = () => {
    if (identityVerificationStatus === 'pending') {
      return <CustomOverlayLoading />;
    }

    if (transactionStatus === TransactionStatusEnum.Completed) {
      return (
        <EmptyState icon={<DoneEmptyState />} headerText={completedText} descriptionText={furtherAssistanceText} />
      );
    }

    if (
      transactionStatus === TransactionStatusEnum.Cancelled ||
      transactionStatus === TransactionStatusEnum.Error ||
      transactionStatus === TransactionStatusEnum.UserCancelled
    ) {
      return (
        <EmptyState
          icon={<StopEmptyState />}
          headerText={requestTimeOutText}
          descriptionText={requestTimeOutHelpText}
        />
      );
    }

    if (identityVerificationStatus === 'error') {
      return (
        <EmptyState
          icon={<ErrorEmptyState />}
          headerText={unexpectedErrorText}
          descriptionText={unexpectedErrorHelpText}
        />
      );
    }

    return null;
  };

  return (
    <ContentContainer fullWidth mTop={'0'}>
      <div className={classes.headerBar}>
        <Logo />
      </div>
      <div className={classes.contentWrap}>{renderStatus()}</div>
    </ContentContainer>
  );
};

export default IdentityVerification;
